import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, User, Phone, Stethoscope, Users, ClipboardList, Edit2, Save, Plus, Trash2, Info, AlertTriangle, Eye, FileText, Briefcase } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, formatDate, formatTime, formatDuration } from '../hooks/useSupabase'
import { supabase } from '../lib/supabase'
import { Button, Card, Input, Select, Textarea, PageHeader, Toast, Badge, Modal } from '../components/ui/SharedUI'

const PROGRAM_OPTIONS = [
  { value: '', label: 'Select program...' },
  { value: 'ICWP', label: 'ICWP' },
  { value: 'CCSP', label: 'CCSP' },
  { value: 'SOURCE', label: 'SOURCE' },
  { value: 'EDWP', label: 'EDWP' },
  { value: 'SFC', label: 'SFC' },
  { value: 'private_pay', label: 'Private pay' },
  { value: 'other', label: 'Other' },
]

const ROLE_LABELS = {
  paid_aide: 'Paid caregiver',
  family_caregiver: 'Family caregiver',
  care_coordinator: 'Care coordinator',
  caregiver_advocate: 'Advocate',
}

export function RecipientDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, profile } = useAuth()

  const [recipient, setRecipient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [shifts, setShifts] = useState([])
  const [toast, setToast] = useState(null)
  const [editSection, setEditSection] = useState(null) // 'basic' | 'routines' | 'contacts' | 'pcp' | 'case_manager' | null

  useEffect(() => { loadData() }, [id])

  async function loadData() {
    setLoading(true)
    try {
      const { data: r, error } = await supabase
        .from('recipients')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      setRecipient(r)

      const { data: s } = await supabase
        .from('shifts')
        .select('*')
        .eq('recipient_id', id)
        .order('clock_in', { ascending: false })
        .limit(50)
      setShifts(s || [])
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  async function updateRecipient(updates) {
    try {
      const { error } = await supabase
        .from('recipients')
        .update(updates)
        .eq('id', id)
      if (error) throw error
      loadData()
      setEditSection(null)
      setToast({ message: 'Updated', type: 'success' })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  // Derive care team roster from data we have
  const careTeam = useMemo(() => {
    if (!recipient) return []

    const team = []

    // The current user (primary caregiver)
    team.push({
      type: 'primary_caregiver',
      name: profile?.name || 'You',
      roles: profile?.roles || [profile?.role].filter(Boolean),
      isYou: true,
    })

    // Observed caregivers derived from shifts
    const observedNames = new Set()
    for (const s of shifts) {
      if (s.authorship === 'observed' && s.observed_caregiver_name) {
        observedNames.add(s.observed_caregiver_name)
      }
    }
    for (const name of Array.from(observedNames).sort()) {
      const shiftCount = shifts.filter(s => s.observed_caregiver_name === name).length
      team.push({
        type: 'observed_caregiver',
        name,
        shiftCount,
      })
    }

    // Case manager
    if (recipient.case_manager?.name) {
      team.push({
        type: 'case_manager',
        name: recipient.case_manager.name,
        organization: recipient.case_manager.organization,
        phone: recipient.case_manager.phone,
        email: recipient.case_manager.email,
      })
    }

    // Emergency contacts
    for (const ec of (recipient.emergency_contacts || [])) {
      team.push({
        type: 'emergency_contact',
        name: ec.name,
        relationship: ec.relationship,
        phone: ec.phone,
      })
    }

    // Primary care physician
    if (recipient.primary_care_physician?.name) {
      team.push({
        type: 'pcp',
        name: recipient.primary_care_physician.name,
        practice: recipient.primary_care_physician.practice,
        phone: recipient.primary_care_physician.phone,
      })
    }

    return team
  }, [recipient, shifts, profile])

  // Shift stats scoped to this recipient
  const shiftStats = useMemo(() => {
    const completed = shifts.filter(s => s.clock_out && !s.is_active)
    const logged = completed.filter(s => s.authorship !== 'observed')
    const observed = completed.filter(s => s.authorship === 'observed')
    const totalLoggedHours = logged.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
    const totalObservedHours = observed.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
    return { logged: logged.length, observed: observed.length, totalLoggedHours, totalObservedHours }
  }, [shifts])

  if (loading) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>
  if (!recipient) return <div style={{ padding: '2rem', color: '#718096' }}>Recipient not found</div>

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <Link to="/recipients" style={{ color: '#2C7A7B', fontSize: '14px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '1rem' }}>
        <ArrowLeft size={14} /> Back to care recipients
      </Link>

      <PageHeader
        title={recipient.name}
        description={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {recipient.program && <Badge>{recipient.program}</Badge>}
            {recipient.agency && <span style={{ fontSize: '14px', color: '#4A5568' }}>· {recipient.agency}</span>}
            {recipient.relationship && <span style={{ fontSize: '14px', color: '#4A5568' }}>· {recipient.relationship}</span>}
          </div>
        }
      />

      {/* Shift summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '1.5rem' }}>
        <StatMini label="Logged shifts" value={shiftStats.logged} sub={`${shiftStats.totalLoggedHours.toFixed(1)}h`} color="#005440" />
        <StatMini label="Observed shifts" value={shiftStats.observed} sub={`${shiftStats.totalObservedHours.toFixed(1)}h`} color="#8b5000" />
        <StatMini label="Total shifts" value={shiftStats.logged + shiftStats.observed} sub="on record" />
      </div>

      {/* Basic info section */}
      <SectionHeader title="Basic information" onEdit={() => setEditSection('basic')} />
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
          <InfoRow label="Program" value={recipient.program} />
          <InfoRow label="Relationship" value={recipient.relationship} />
          <InfoRow label="Agency" value={recipient.agency} />
          <InfoRow label="Waiver case number" value={recipient.waiver_case_number} />
          <InfoRow label="Waiver review date" value={recipient.waiver_review_date ? formatDate(recipient.waiver_review_date) : null} />
          <InfoRow label="Date of birth" value={recipient.date_of_birth ? formatDate(recipient.date_of_birth) : null} />
        </div>
      </Card>

      {/* Care team section */}
      <SectionHeader title="Care team" />
      <Card style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontSize: '13px', color: '#718096', marginBottom: '12px' }}>
          Everyone involved in {recipient.name}'s care — derived from your records and contacts.
        </p>
        <div style={{ display: 'grid', gap: '8px' }}>
          {careTeam.map((member, i) => <CareTeamMember key={i} member={member} />)}
          {careTeam.length === 1 && (
            <div style={{ fontSize: '13px', color: '#A0AEC0', fontStyle: 'italic', textAlign: 'center', padding: '10px' }}>
              Add emergency contacts, case manager, or PCP below to complete the care team roster.
            </div>
          )}
        </div>
      </Card>

      {/* Care routines section */}
      <SectionHeader title="Care routines and preferences" onEdit={() => setEditSection('routines')} />
      <Card style={{ marginBottom: '1.5rem' }}>
        {recipient.care_routines ? (
          <div style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {recipient.care_routines}
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#A0AEC0', fontStyle: 'italic' }}>
            No care routines documented. Add daily routines, care preferences, and helpful context for anyone providing care.
          </div>
        )}
      </Card>

      {/* Emergency contacts section */}
      <SectionHeader
        title="Emergency contacts"
        onEdit={() => setEditSection('contacts')}
        actionLabel={recipient.emergency_contacts?.length ? 'Edit' : 'Add contacts'}
      />
      <Card style={{ marginBottom: '1.5rem' }}>
        {recipient.emergency_contacts?.length > 0 ? (
          <div style={{ display: 'grid', gap: '10px' }}>
            {recipient.emergency_contacts.map((ec, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < recipient.emergency_contacts.length - 1 ? '1px solid #EDF2F7' : 'none' }}>
                <div style={{ padding: '8px', background: '#E1F5EE', borderRadius: '8px' }}>
                  <Phone size={16} style={{ color: '#005440' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>{ec.name}</div>
                  <div style={{ fontSize: '13px', color: '#718096' }}>
                    {ec.relationship && `${ec.relationship} · `}{ec.phone}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#A0AEC0', fontStyle: 'italic' }}>
            No emergency contacts. Add family members, close friends, or others who should be reached in an emergency.
          </div>
        )}
      </Card>

      {/* Primary care physician section */}
      <SectionHeader
        title="Primary care physician"
        onEdit={() => setEditSection('pcp')}
        actionLabel={recipient.primary_care_physician?.name ? 'Edit' : 'Add PCP'}
      />
      <Card style={{ marginBottom: '1.5rem' }}>
        {recipient.primary_care_physician?.name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '10px', background: '#E1F5EE', borderRadius: '8px' }}>
              <Stethoscope size={18} style={{ color: '#005440' }} />
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>
                {recipient.primary_care_physician.name}
              </div>
              <div style={{ fontSize: '13px', color: '#718096' }}>
                {recipient.primary_care_physician.practice && `${recipient.primary_care_physician.practice} · `}
                {recipient.primary_care_physician.phone}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#A0AEC0', fontStyle: 'italic' }}>
            No PCP on file. Directory information only — no medical records or diagnoses (that requires a HIPAA Business Associate Agreement).
          </div>
        )}
      </Card>

      {/* Case manager section */}
      <SectionHeader
        title="Case manager"
        onEdit={() => setEditSection('case_manager')}
        actionLabel={recipient.case_manager?.name ? 'Edit' : 'Add case manager'}
      />
      <Card style={{ marginBottom: '1.5rem' }}>
        {recipient.case_manager?.name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '10px', background: '#E1F5EE', borderRadius: '8px' }}>
              <Briefcase size={18} style={{ color: '#005440' }} />
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>
                {recipient.case_manager.name}
              </div>
              <div style={{ fontSize: '13px', color: '#718096' }}>
                {recipient.case_manager.organization && `${recipient.case_manager.organization} · `}
                {recipient.case_manager.phone}
                {recipient.case_manager.email && ` · ${recipient.case_manager.email}`}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#A0AEC0', fontStyle: 'italic' }}>
            No case manager on file. Add the case manager overseeing {recipient.name}'s waiver.
          </div>
        )}
      </Card>

      {/* Recent shifts */}
      <SectionHeader title={`Recent shifts (${shifts.length})`} />
      <Card style={{ marginBottom: '1.5rem' }}>
        {shifts.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#A0AEC0', fontStyle: 'italic', padding: '10px' }}>
            No shifts logged for {recipient.name} yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '6px' }}>
            {shifts.slice(0, 15).map(s => (
              <ShiftMini key={s.id} shift={s} />
            ))}
            {shifts.length > 15 && (
              <div style={{ fontSize: '12px', color: '#718096', textAlign: 'center', padding: '8px' }}>
                + {shifts.length - 15} more shifts (see Shifts page)
              </div>
            )}
          </div>
        )}
      </Card>

      {/* PHI notice */}
      <Card style={{ marginBottom: '1.5rem', background: '#FCF5E8', borderLeft: '3px solid #8b5000' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <Info size={16} style={{ color: '#8b5000', marginTop: '2px', flexShrink: 0 }} />
          <div style={{ fontSize: '13px', color: '#8b5000', lineHeight: 1.6 }}>
            <strong>About medical information.</strong> KindredCare does not currently store Protected Health Information (PHI) like medications, diagnoses, or medical history. The fields on this page are for directory and contact information only. PHI storage requires a HIPAA Business Associate Agreement, which is on the Phase 2 roadmap.
          </div>
        </div>
      </Card>

      {/* Edit modals */}
      <EditModals
        section={editSection}
        recipient={recipient}
        onClose={() => setEditSection(null)}
        onSave={updateRecipient}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════

function SectionHeader({ title, onEdit, actionLabel = 'Edit' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
      <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </h2>
      {onEdit && (
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Edit2 size={13} /> {actionLabel}
        </Button>
      )}
    </div>
  )
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '14px', color: value ? '#1A202C' : '#A0AEC0', fontWeight: value ? 500 : 400 }}>
        {value || '—'}
      </div>
    </div>
  )
}

function StatMini({ label, value, sub, color = '#2C7A7B' }) {
  return (
    <div style={{ padding: '14px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
      <div style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 600, color, fontFamily: "'Playfair Display', serif" }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>{sub}</div>}
    </div>
  )
}

function CareTeamMember({ member }) {
  const config = {
    primary_caregiver: { icon: User, bg: '#E1F5EE', color: '#005440', label: 'Primary caregiver' },
    observed_caregiver: { icon: Eye, bg: '#FCF5E8', color: '#8b5000', label: 'Other caregiver' },
    case_manager: { icon: Briefcase, bg: '#E1F5EE', color: '#005440', label: 'Case manager' },
    emergency_contact: { icon: Phone, bg: '#FAECE7', color: '#993C1D', label: 'Emergency contact' },
    pcp: { icon: Stethoscope, bg: '#E1F5EE', color: '#005440', label: 'Primary care physician' },
  }[member.type] || { icon: User, bg: '#F7FAFC', color: '#4A5568', label: '' }

  const Icon = config.icon

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: '#FAFBFC', borderRadius: '8px' }}>
      <div style={{ padding: '8px', background: config.bg, borderRadius: '8px' }}>
        <Icon size={16} style={{ color: config.color }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '2px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>
            {member.name}
            {member.isYou && <span style={{ fontSize: '12px', color: '#718096', fontWeight: 400 }}> (you)</span>}
          </span>
          <Badge variant={member.type === 'observed_caregiver' ? 'amber' : 'default'}>{config.label}</Badge>
        </div>
        <div style={{ fontSize: '12px', color: '#718096' }}>
          {member.roles?.length > 0 && member.roles.map(r => ROLE_LABELS[r] || r).join(' · ')}
          {member.shiftCount != null && `${member.shiftCount} shift${member.shiftCount === 1 ? '' : 's'} documented`}
          {member.relationship && `${member.relationship}${member.phone ? ' · ' + member.phone : ''}`}
          {member.organization && `${member.organization}${member.phone ? ' · ' + member.phone : ''}`}
          {member.practice && `${member.practice}${member.phone ? ' · ' + member.phone : ''}`}
        </div>
      </div>
    </div>
  )
}

function ShiftMini({ shift }) {
  const isObserved = shift.authorship === 'observed'
  const caregiverName = isObserved ? shift.observed_caregiver_name : 'You'

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px',
      background: isObserved ? '#FDFCF7' : '#FFFFFF',
      borderLeft: `3px solid ${isObserved ? '#8b5000' : '#2C7A7B'}`,
      borderRadius: '4px',
    }}>
      <div style={{ minWidth: '80px', fontSize: '13px', fontWeight: 500, color: '#1A202C' }}>
        {formatDate(shift.date)}
      </div>
      <div style={{ fontSize: '13px', color: '#718096', minWidth: '110px' }}>
        {formatTime(shift.clock_in)}–{formatTime(shift.clock_out)}
      </div>
      <div style={{ fontSize: '13px', color: '#4A5568', flex: 1 }}>
        {caregiverName}
      </div>
      {isObserved && <Badge variant="amber">Observed</Badge>}
      {shift.incident && <Badge variant="coral">Incident</Badge>}
      <div style={{ fontSize: '13px', color: '#1A202C', fontWeight: 500 }}>
        {shift.clock_out ? `${((new Date(shift.clock_out) - new Date(shift.clock_in)) / 3600000).toFixed(1)}h` : '—'}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Edit modals
// ═══════════════════════════════════════════════════════════════

function EditModals({ section, recipient, onClose, onSave }) {
  if (!section) return null

  if (section === 'basic') return <BasicInfoModal recipient={recipient} onClose={onClose} onSave={onSave} />
  if (section === 'routines') return <RoutinesModal recipient={recipient} onClose={onClose} onSave={onSave} />
  if (section === 'contacts') return <ContactsModal recipient={recipient} onClose={onClose} onSave={onSave} />
  if (section === 'pcp') return <PCPModal recipient={recipient} onClose={onClose} onSave={onSave} />
  if (section === 'case_manager') return <CaseManagerModal recipient={recipient} onClose={onClose} onSave={onSave} />
  return null
}

function BasicInfoModal({ recipient, onClose, onSave }) {
  const [form, setForm] = useState({
    name: recipient.name || '',
    relationship: recipient.relationship || '',
    program: recipient.program || '',
    agency: recipient.agency || '',
    waiver_case_number: recipient.waiver_case_number || '',
    waiver_review_date: recipient.waiver_review_date || '',
    date_of_birth: recipient.date_of_birth || '',
  })

  return (
    <Modal open={true} onClose={onClose} title="Basic information" width={520}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form) }}>
        <Input label="Name" id="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
        <Input label="Your relationship" id="rel" value={form.relationship}
          onChange={e => setForm({ ...form, relationship: e.target.value })}
          placeholder="e.g. Mother, Father, Client" />
        <Select label="Program" id="program" value={form.program}
          onChange={e => setForm({ ...form, program: e.target.value })} options={PROGRAM_OPTIONS} />
        <Input label="Agency" id="agency" value={form.agency}
          onChange={e => setForm({ ...form, agency: e.target.value })}
          placeholder="e.g. Serenity Health Care Services" />
        <Input label="Waiver case number" id="case_num" value={form.waiver_case_number}
          onChange={e => setForm({ ...form, waiver_case_number: e.target.value })} />
        <Input label="Waiver annual review date" id="review_date" type="date" value={form.waiver_review_date}
          onChange={e => setForm({ ...form, waiver_review_date: e.target.value })} />
        <Input label="Date of birth" id="dob" type="date" value={form.date_of_birth}
          onChange={e => setForm({ ...form, date_of_birth: e.target.value })} />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit"><Save size={14} /> Save</Button>
        </div>
      </form>
    </Modal>
  )
}

function RoutinesModal({ recipient, onClose, onSave }) {
  const [text, setText] = useState(recipient.care_routines || '')

  return (
    <Modal open={true} onClose={onClose} title="Care routines and preferences" width={560}>
      <p style={{ fontSize: '13px', color: '#4A5568', marginBottom: '12px', lineHeight: 1.6 }}>
        Document daily routines, care preferences, dietary needs, mobility considerations, and other context that helps anyone providing care. This is not medical information — save medications and diagnoses for the emergency care sheet (coming in Phase 2 with HIPAA compliance).
      </p>
      <Textarea label="Care routines" id="routines" value={text}
        onChange={e => setText(e.target.value)}
        placeholder="e.g. Morning routine starts at 7am with breakfast in bed. Prefers oatmeal or eggs. Bath before 10am. Wheelchair transfer requires two-person assist. Reading time in the afternoon..."
        style={{ minHeight: '200px' }} />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave({ care_routines: text })}><Save size={14} /> Save</Button>
      </div>
    </Modal>
  )
}

function ContactsModal({ recipient, onClose, onSave }) {
  const [contacts, setContacts] = useState(recipient.emergency_contacts || [])

  function addContact() {
    setContacts([...contacts, { name: '', relationship: '', phone: '' }])
  }

  function updateContact(index, field, value) {
    setContacts(contacts.map((c, i) => i === index ? { ...c, [field]: value } : c))
  }

  function removeContact(index) {
    setContacts(contacts.filter((_, i) => i !== index))
  }

  return (
    <Modal open={true} onClose={onClose} title="Emergency contacts" width={560}>
      <p style={{ fontSize: '13px', color: '#4A5568', marginBottom: '12px', lineHeight: 1.6 }}>
        Add family members, close friends, or others who should be contacted in an emergency.
      </p>

      <div style={{ display: 'grid', gap: '10px', marginBottom: '1rem' }}>
        {contacts.map((c, i) => (
          <Card key={i} style={{ padding: '12px', background: '#FAFBFC' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', color: '#718096', fontWeight: 500 }}>Contact {i + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => removeContact(i)}>
                <Trash2 size={13} />
              </Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Input label="Name" id={`c-name-${i}`} value={c.name}
                onChange={e => updateContact(i, 'name', e.target.value)}
                placeholder="e.g. Sarah Holloway" />
              <Input label="Relationship" id={`c-rel-${i}`} value={c.relationship}
                onChange={e => updateContact(i, 'relationship', e.target.value)}
                placeholder="e.g. Daughter" />
            </div>
            <Input label="Phone" id={`c-phone-${i}`} type="tel" value={c.phone}
              onChange={e => updateContact(i, 'phone', e.target.value)}
              placeholder="e.g. (555) 123-4567" />
          </Card>
        ))}
      </div>

      <Button variant="secondary" onClick={addContact}>
        <Plus size={14} /> Add contact
      </Button>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave({ emergency_contacts: contacts.filter(c => c.name && c.phone) })}>
          <Save size={14} /> Save
        </Button>
      </div>
    </Modal>
  )
}

function PCPModal({ recipient, onClose, onSave }) {
  const [form, setForm] = useState(recipient.primary_care_physician || { name: '', practice: '', phone: '' })

  return (
    <Modal open={true} onClose={onClose} title="Primary care physician" width={480}>
      <p style={{ fontSize: '13px', color: '#4A5568', marginBottom: '12px', lineHeight: 1.6 }}>
        Directory contact information only. Do not enter diagnoses, medications, or medical history.
      </p>
      <Input label="Doctor's name" id="pcp-name" value={form.name}
        onChange={e => setForm({ ...form, name: e.target.value })} />
      <Input label="Practice or clinic" id="pcp-practice" value={form.practice}
        onChange={e => setForm({ ...form, practice: e.target.value })} />
      <Input label="Phone" id="pcp-phone" type="tel" value={form.phone}
        onChange={e => setForm({ ...form, phone: e.target.value })} />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave({ primary_care_physician: form.name ? form : null })}>
          <Save size={14} /> Save
        </Button>
      </div>
    </Modal>
  )
}

function CaseManagerModal({ recipient, onClose, onSave }) {
  const [form, setForm] = useState(recipient.case_manager || { name: '', organization: '', phone: '', email: '' })

  return (
    <Modal open={true} onClose={onClose} title="Case manager" width={480}>
      <p style={{ fontSize: '13px', color: '#4A5568', marginBottom: '12px', lineHeight: 1.6 }}>
        The case manager overseeing {recipient.name}'s waiver services.
      </p>
      <Input label="Name" id="cm-name" value={form.name}
        onChange={e => setForm({ ...form, name: e.target.value })}
        placeholder="e.g. Annette Barnes" />
      <Input label="Organization" id="cm-org" value={form.organization}
        onChange={e => setForm({ ...form, organization: e.target.value })}
        placeholder="e.g. 360 Cares" />
      <Input label="Phone" id="cm-phone" type="tel" value={form.phone}
        onChange={e => setForm({ ...form, phone: e.target.value })} />
      <Input label="Email" id="cm-email" type="email" value={form.email}
        onChange={e => setForm({ ...form, email: e.target.value })} />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave({ case_manager: form.name ? form : null })}>
          <Save size={14} /> Save
        </Button>
      </div>
    </Modal>
  )
}
